import { createContext, useContext, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { PageLoader } from '../shared/ui/PageLoader.js';
import type { BusinessDoc } from '../shared/api/converters.js';
import { useAuth } from '../features/auth/index.js';
import { setupGateRedirect, writeLastActive } from './activeBusiness.js';
import { useMyBusinesses } from './BusinessesContext.js';

const ActiveBusinessContext = createContext<BusinessDoc | null>(null);

/**
 * Resolves the `:businessId` route param against the account's businesses and
 * renders the business-scoped shell (doc 07 §8.2–8.3). Owns the guard logic that
 * used to be copy-pasted per page: loading → spinner, unknown/foreign id →
 * `/businesses`, empty account → `/businesses/new`.
 */
export function ActiveBusinessLayout() {
  const { businessId } = useParams<{ businessId: string }>();
  const { businesses, loading } = useMyBusinesses();
  const { user } = useAuth();
  const location = useLocation();

  const business = businesses.find((b) => b.id === businessId) ?? null;

  useEffect(() => {
    if (business && user) writeLastActive(user.uid, business.id);
  }, [business, user]);

  if (loading) return <PageLoader />;
  if (businesses.length === 0) return <Navigate to="/businesses/new" replace />;
  // Unknown or foreign id — never render another tenant's shell.
  if (!business) return <Navigate to="/businesses" replace />;

  // ── The setup gate ───────────────────────────────────────────────────────
  //
  // Unfinished setup LOCKS the business: until every step is satisfied — the
  // last of them being a plan — the only routes it renders are the wizard and
  // the payment screens it hands off to.
  //
  // ONE gate rather than a guard per entry point, and that matters more than it
  // looks: `/calls`, `/settings` and `/test` resolve through `LegacyRedirect`,
  // the switcher rewrites the current path onto another business, `/` resolves
  // through `homePath`, and the businesses list links straight to `/b/:id`.
  // Each of those would have been its own way in. They all land here.
  //
  // The rule itself is pure and lives in `activeBusiness.ts`, where it can be
  // tested against every route instead of only observed through a router.
  const redirect = setupGateRedirect(business, location.pathname);
  if (redirect) return <Navigate to={redirect} replace />;

  return (
    <ActiveBusinessContext.Provider value={business}>
      <Outlet />
    </ActiveBusinessContext.Provider>
  );
}

/** The business the current `/b/:businessId/...` route is scoped to (never null). */
export function useActiveBusiness(): BusinessDoc {
  const business = useContext(ActiveBusinessContext);
  if (!business) {
    throw new Error('useActiveBusiness must be used inside ActiveBusinessLayout');
  }
  return business;
}
